/**
 * WorkflowAggregateContainer.tsx — Parallel aggregate 容器行
 *
 * 职责：
 *     waiting (k/n) / running / Done；合并结果 teaser；D24 壳行语法。
 *
 * 链路位置：
 *     WorkflowChrome → scope aggregate 节点。
 *
 * 正文：aggregate 无 Agent loop（D24 §8.2），择优/合并结果走 content_blocks；
 * done 态用 WorkflowMarkdownPreview 渲染 GFM（表格/代码块）。
 */
import { useCallback, useEffect, useMemo, useState, memo, type CSSProperties } from 'react';

import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import type { SessionTree } from '../../types/tree';
import { AgentLoopView } from '../agentLoop/AgentLoopView';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import { WorkflowExpandTrigger } from './WorkflowExpandTrigger';
import { WorkflowMarkdownPreview } from './WorkflowMarkdownPreview';
import {
  buildAggregateLineMeta,
  buildAggregateStatusLabel,
  mapAggregateUiStatus,
} from './workflowContainerLineUtils';
import {
  workflowContainerNodeVisualEqual,
  workflowContainerViewPropsEqual,
} from './workflowContainerMemo';

export interface WorkflowAggregateContainerProps {
  node: WorkflowContainerRuntimeNode;
  loopTree?: SessionTree;
  siblingItems?: WorkflowContainerRuntimeNode[];
  depth?: number;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
}

function mergeContentPreview(node: WorkflowContainerRuntimeNode): string {
  const blocks = node.content_blocks;
  if (blocks.length === 0) return '';
  return blocks
    .map((block) => block.preview?.trim() ?? '')
    .filter(Boolean)
    .join('\n\n');
}

export function WorkflowAggregateContainerInner({
  node,
  loopTree,
  siblingItems = [],
  depth = 1,
  virtualized,
  virtualizeThreshold,
  groupParallelTools,
}: WorkflowAggregateContainerProps) {
  const isRunning = node.status === 'running' || node.status === 'retrying';
  const isWaiting = node.status === 'pending' || node.status === 'blocked';
  const isDone = node.status === 'completed' || node.status === 'failed';
  const contentPreview = useMemo(() => mergeContentPreview(node), [node]);
  const waitingTeaser = isWaiting ? (contentPreview || node.subtitle || '') : '';
  const testId = `lax-workflow-aggregate-${node.scope_key}`;
  const uiStatus = mapAggregateUiStatus(node);
  const statusLabel = buildAggregateStatusLabel(node, siblingItems);

  const [expanded, setExpanded] = useState(isRunning);

  useEffect(() => {
    if (isRunning) setExpanded(true);
    if (isDone) setExpanded(false);
  }, [isRunning, isDone]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const shellToggle =
    isRunning ? toggleExpanded
    : isDone && expanded ? toggleExpanded
      : undefined;

  const markdownPreview = contentPreview ? (
    <WorkflowMarkdownPreview
      content={contentPreview}
      testId={`${testId}-markdown`}
      className="lax-workflow-aggregate__markdown lax-workflow-markdown-preview"
    />
  ) : null;

  return (
    <div
      className={`lax-workflow-aggregate lax-workflow-stage-container lax-workflow-stage-container--${
        isWaiting ? 'pending' : isDone ? 'done' : 'running'
      }`}
      data-testid={testId}
      data-container-scope="aggregate"
      style={{ '--lax-workflow-depth': depth } as CSSProperties}
    >
      <WorkflowContainerLine
        title={node.title}
        description={node.subtitle || undefined}
        uiStatus={uiStatus}
        meta={buildAggregateLineMeta(node)}
        statusLabel={statusLabel}
        onToggle={shellToggle}
        interactive={isRunning}
        expanded={expanded}
        titleTestId={`${testId}-title`}
        descriptionTestId={`${testId}-desc`}
      />

      {isWaiting && waitingTeaser ? (
        <div className="lax-workflow-aggregate__waiting-teaser" data-testid={`${testId}-waiting`}>
          <span className="lax-workflow-aggregate__waiting-prefix">⎿ </span>
          <WorkflowMarkdownPreview
            content={waitingTeaser}
            className="lax-workflow-aggregate__markdown lax-workflow-markdown-preview"
          />
        </div>
      ) : null}

      {expanded && isRunning && loopTree ? (
        <div className="lax-workflow-stage-container__body" data-testid={`${testId}-body`}>
          <AgentLoopView
            tree={loopTree}
            showTaskListFooter={false}
            virtualized={virtualized}
            virtualizeThreshold={virtualizeThreshold}
            groupParallelTools={groupParallelTools}
          />
        </div>
      ) : null}

      {expanded && isRunning && !loopTree && contentPreview ? (
        <div
          className="lax-workflow-stage-container__body lax-workflow-stage-container__content-preview"
          data-testid={`${testId}-body`}
        >
          {markdownPreview}
        </div>
      ) : null}

      {isDone && !expanded ? (
        <div className="lax-workflow-aggregate__done" data-testid={`${testId}-done`}>
          <WorkflowExpandTrigger
            summary={`${node.title} · done`}
            onToggle={() => setExpanded(true)}
            expanded={false}
            expandHint={contentPreview ? '(click to expand)' : undefined}
            testId={`${testId}-done-trigger`}
          />
          {markdownPreview ? (
            <div className="lax-workflow-aggregate__done-preview" data-testid={`${testId}-teaser`}>
              {markdownPreview}
            </div>
          ) : null}
        </div>
      ) : null}

      {isDone && expanded && loopTree ? (
        <div className="lax-workflow-stage-container__body" data-testid={`${testId}-body`}>
          <AgentLoopView
            tree={loopTree}
            showTaskListFooter={false}
            virtualized={virtualized}
            virtualizeThreshold={virtualizeThreshold}
            groupParallelTools={groupParallelTools}
          />
        </div>
      ) : null}

      {isDone && expanded && !loopTree && contentPreview ? (
        <div
          className="lax-workflow-stage-container__body lax-workflow-stage-container__content-preview"
          data-testid={`${testId}-body`}
        >
          {markdownPreview}
        </div>
      ) : null}
    </div>
  );
}

function workflowAggregateContainerPropsEqual(
  prev: WorkflowAggregateContainerProps,
  next: WorkflowAggregateContainerProps,
): boolean {
  if (prev.loopTree !== next.loopTree) return false;
  if (!workflowContainerViewPropsEqual(prev, next)) return false;
  if (prev.siblingItems.length !== next.siblingItems.length) return false;
  for (let i = 0; i < prev.siblingItems.length; i += 1) {
    if (!workflowContainerNodeVisualEqual(prev.siblingItems[i]!, next.siblingItems[i]!)) {
      return false;
    }
  }
  return workflowContainerNodeVisualEqual(prev.node, next.node);
}

export const WorkflowAggregateContainer = memo(
  WorkflowAggregateContainerInner,
  workflowAggregateContainerPropsEqual,
);
